/** @module @liquidefy/code-editor/circuits/git-workspace @version 1.0.0 @license LQD Research LLC */

/**
 * Git Workspace Circuit — Tier 1
 *
 * Git operations with diff computation, three-way merge, conflict resolution,
 * unified formatting, and code review in one call.
 *
 * Composes: git/git.ts, diff/compute.ts, diff/merge.ts, diff/render.ts, diff/review.ts
 *
 * @rust git_workspace.rs — false; 4/2/26; sm
 */

// ═══════════════════════════════════════════════════════════════════════════════
// §1 IMPORTS
// ═══════════════════════════════════════════════════════════════════════════════

import type { CircuitHandle, CircuitHealthReport, CircuitStats } from './circuit-types.js';
import {
  buildHealthReport,
  emptyStats,
  recordSuccess,
  recordError,
  healthFromStats,
  formatStatsDetails,
} from './circuit-types.js';

import type {
  DiffAlgorithm,
  DiffResult,
  DiffStats,
  ThreeWayMergeState,
  MergeResolutionStrategy,
  ReviewState,
  EditorResult,
  GitRepositoryState,
} from '../code-editor-types.js';

import {
  ok,
  err,
} from '../code-editor.js';

import {
  createRepositoryState,
  getStatus,
} from '../git/git.js';
import type { WorkingTreeStatus } from '../git/git.js';

import { computeDiff, computeStats } from '../diff/compute.js';
import { threeWayMerge, resolveConflict } from '../diff/merge.js';
import { formatUnifiedDiff } from '../diff/render.js';
import { createReviewState } from '../diff/review.js';
import type { FullReviewState } from '../diff/review.js';

// ═══════════════════════════════════════════════════════════════════════════════
// §2 CONFIG & HANDLE INTERFACES
// ═══════════════════════════════════════════════════════════════════════════════

export interface GitWorkspaceConfig {
  readonly repositoryPath?: string;
}

export interface GitWorkspaceHandle extends CircuitHandle {
  status(): WorkingTreeStatus;
  diff(oldContent: string, newContent: string, algorithm?: DiffAlgorithm): EditorResult<DiffResult>;
  diffStats(diff: DiffResult): DiffStats;
  formatUnified(diff: DiffResult, contextLines?: number): string;
  merge(base: string, local: string, remote: string): EditorResult<ThreeWayMergeState>;
  resolveConflict(state: ThreeWayMergeState, conflictId: string, strategy: MergeResolutionStrategy): EditorResult<ThreeWayMergeState>;
  createReview(): FullReviewState;
  stats(): CircuitStats;
  health(): CircuitHealthReport;
  destroy(): void;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §3 FACTORY
// ═══════════════════════════════════════════════════════════════════════════════

export function createGitWorkspace(config: GitWorkspaceConfig = {}): GitWorkspaceHandle {
  // 1. Extract config with defaults
  const repositoryPath = config.repositoryPath ?? '/workspace';

  // 2. Initialize internal state
  let stats: CircuitStats = emptyStats();
  let destroyed = false;
  const createdAt = Date.now();

  // 3. Create git repository state
  const repoResult = createRepositoryState({ rootPath: repositoryPath });
  if (!repoResult.success) {
    throw new Error(`GitWorkspace: failed to create repository state — ${repoResult.message}`);
  }
  let repoState: GitRepositoryState = repoResult.value;

  // 4. Track last diff for review creation
  let lastDiffId: string | null = null;

  // 5. Guard
  function guardDestroyed(): void {
    if (destroyed) throw new Error('Circuit destroyed');
  }

  // ═════════════════════════════════════════════════════════════════════════════
  // §4 DOMAIN METHODS
  // ═════════════════════════════════════════════════════════════════════════════

  function doStatus(): WorkingTreeStatus {
    guardDestroyed();
    stats = recordSuccess(stats);
    return getStatus(repoState);
  }

  function doDiff(
    oldContent: string,
    newContent: string,
    algorithm?: DiffAlgorithm,
  ): EditorResult<DiffResult> {
    guardDestroyed();
    const result = computeDiff(oldContent, newContent, algorithm);
    if (result.success) {
      lastDiffId = result.value.id;
      stats = recordSuccess(stats);
    } else {
      stats = recordError(stats);
    }
    return result;
  }

  function doDiffStats(diff: DiffResult): DiffStats {
    guardDestroyed();
    stats = recordSuccess(stats);
    return diff.stats;
  }

  function doFormatUnified(diff: DiffResult, contextLines?: number): string {
    guardDestroyed();
    stats = recordSuccess(stats);
    return formatUnifiedDiff(diff, contextLines);
  }

  function doMerge(
    base: string,
    local: string,
    remote: string,
  ): EditorResult<ThreeWayMergeState> {
    guardDestroyed();
    const result = threeWayMerge(base, local, remote);
    if (result.success) {
      stats = recordSuccess(stats);
    } else {
      stats = recordError(stats);
    }
    return result;
  }

  function doResolveConflict(
    state: ThreeWayMergeState,
    conflictId: string,
    strategy: MergeResolutionStrategy,
  ): EditorResult<ThreeWayMergeState> {
    guardDestroyed();
    const result = resolveConflict(state, conflictId, strategy);
    if (result.success) {
      stats = recordSuccess(stats);
    } else {
      stats = recordError(stats);
    }
    return result;
  }

  function doCreateReview(): FullReviewState {
    guardDestroyed();
    const diffId = lastDiffId ?? 'no-diff';
    const review = createReviewState(diffId as any);
    stats = recordSuccess(stats);
    return review;
  }

  // ═════════════════════════════════════════════════════════════════════════════
  // §5 FROZEN HANDLE
  // ═════════════════════════════════════════════════════════════════════════════

  return Object.freeze({
    name: 'git-workspace',

    status: doStatus,
    diff: doDiff,
    diffStats: doDiffStats,
    formatUnified: doFormatUnified,
    merge: doMerge,
    resolveConflict: doResolveConflict,
    createReview: doCreateReview,

    stats(): CircuitStats {
      return { ...stats, uptime: Date.now() - createdAt };
    },

    health(): CircuitHealthReport {
      if (destroyed) return buildHealthReport('git-workspace', 'destroyed', 'Circuit destroyed', createdAt);
      return buildHealthReport('git-workspace', healthFromStats(stats), formatStatsDetails(stats), createdAt);
    },

    destroy(): void {
      destroyed = true;
    },
  });
}
